import { useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { useDidUpdate } from '@toss/react';
import { useDelayedLoading } from '@/hooks';
import { cn } from '@/utils/tailwind.util';
import { LIST_GRID_CLASSNAME } from './listGrid';
type InfiniteListViewProps<T> = {
  items: T[] | undefined;
  isLoading: boolean;
  isError: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage?: boolean;
  renderItem: (item: T, index: number) => React.ReactNode;
  EmptyComponent?: React.ReactNode;
  ErrorComponent?: React.ReactNode;
  LoadingComponent?: React.ReactNode;
  HeaderComponent?: React.ReactNode;
  /**
   * 열 규칙. 기본은 카드 목록 규칙(`LIST_GRID_CLASSNAME`)이다.
   *
   * 다른 열 규칙을 쓰는 목록(예: 보드 2열)은 `className` 으로 기본 규칙을 덮어쓰지 말고
   * 여기로 넘긴다 — 덮어쓰기는 어떤 클래스가 이기는지가 병합 순서에 달려 있어 깨지기 쉽다.
   */
  gridClassName?: string;
  className?: string;
};
const DEFAULT_LOADING_COMPONENT = <>Loading...</>;

/**
 * 무한 스크롤 목록 골격. 무엇의 목록인지 모른다 — 퀴즈·폴더·보드가 `renderItem` 으로
 * 자기 아이템을 그린다.
 */
export function InfiniteListView<T>({
  items,
  isLoading,
  isError,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage = false,
  renderItem,
  EmptyComponent = null,
  ErrorComponent = null,
  LoadingComponent = DEFAULT_LOADING_COMPONENT,
  HeaderComponent = null,
  gridClassName = LIST_GRID_CLASSNAME,
  className = ''
}: InfiniteListViewProps<T>) {
  const {
    ref,
    inView
  } = useInView({
    triggerOnce: false,
    threshold: 0.1
  });
  const delayedLoading = useDelayedLoading(isLoading, 2000);
  const isFetchingNextPageRef = useRef(isFetchingNextPage);
  useEffect(() => {
    isFetchingNextPageRef.current = isFetchingNextPage;
  }, [isFetchingNextPage]);
  useDidUpdate(() => {
    if (inView && hasNextPage && !isFetchingNextPageRef.current) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage]);
  if (isLoading || !items) return null;
  if (delayedLoading) return <>{LoadingComponent}</>;
  if (isError) return <>{ErrorComponent}</>;
  if (items.length === 0 && !hasNextPage) return <>{EmptyComponent}</>;
  return <div className={cn(gridClassName, 'pb-lg', className)} data-sentry-component="InfiniteListView" data-sentry-source-file="InfiniteListView.tsx">
      {HeaderComponent}
      {items.map((item, idx) => renderItem(item, idx))}
      <div key="in-view-trigger" ref={ref} />
    </div>;
}